import React, { useState, useMemo, useEffect } from 'react';
import type { MatchPairsScreen as MatchPairsScreenType } from '../../types/course';
import { playTickSound, playCorrectSound, playIncorrectSound } from '../../utils/sound';

interface Props {
  screen: MatchPairsScreenType;
  onInteractionComplete: () => void;
  completed: boolean;
}

export const MatchPairsScreen: React.FC<Props> = ({
  screen,
  onInteractionComplete,
  completed,
}) => {
  // Matched pair indices
  const [matched, setMatched] = useState<Set<number>>(
    () => completed ? new Set(screen.pairs.map((_, i) => i)) : new Set()
  );
  const [selectedLeft, setSelectedLeft] = useState<number | null>(null);
  const [selectedRight, setSelectedRight] = useState<number | null>(null);
  const [wrongPair, setWrongPair] = useState<{ left: number; right: number } | null>(null);

  // Shuffle right column
  const shuffledRightIndices = useMemo(() => {
    const indices = screen.pairs.map((_, i) => i);
    // Simple deterministic shuffle
    const seed = screen.title.length;
    return indices.sort((a, b) => {
      const ha = ((a + 1) * 37 + seed) % 100;
      const hb = ((b + 1) * 37 + seed) % 100;
      return ha - hb;
    });
  }, [screen.pairs, screen.title]);

  const allMatched = matched.size === screen.pairs.length;

  useEffect(() => {
    if (allMatched && !completed) {
      onInteractionComplete();
      playCorrectSound();
    }
  }, [allMatched, completed, onInteractionComplete]);

  const tryMatch = (leftIdx: number, rightIdx: number) => {
    // rightIdx refers to the original pair index
    if (leftIdx === rightIdx) {
      // Correct match
      playTickSound();
      setMatched((prev) => new Set(prev).add(leftIdx));
      setSelectedLeft(null);
      setSelectedRight(null);
      setWrongPair(null);
    } else {
      // Wrong match
      playIncorrectSound();
      setWrongPair({ left: leftIdx, right: rightIdx });
      setTimeout(() => {
        setWrongPair(null);
        setSelectedLeft(null);
        setSelectedRight(null);
      }, 600);
    }
  };

  const handleLeftTap = (index: number) => {
    if (matched.has(index)) return;
    setSelectedLeft(index);
    if (selectedRight !== null) {
      tryMatch(index, selectedRight);
    }
  };

  const handleRightTap = (originalIndex: number) => {
    if (matched.has(originalIndex)) return;
    setSelectedRight(originalIndex);
    if (selectedLeft !== null) {
      tryMatch(selectedLeft, originalIndex);
    }
  };

  return (
    <div className="flex flex-col gap-4 px-1">
      {screen.icon && <div className="text-3xl text-center">{screen.icon}</div>}
      <h3 className="text-lg font-semibold font-serif text-[var(--clue-text-primary)] text-center">
        {screen.title}
      </h3>

      {screen.body && (
        <>
          <p className="text-sm leading-relaxed text-[var(--clue-text-secondary)]">
            {screen.body}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <div className="flex-1 h-px bg-[var(--clue-border-mid)]" />
            <span className="text-[10px] font-semibold text-[var(--clue-text-tertiary)] uppercase tracking-widest">Your turn</span>
            <div className="flex-1 h-px bg-[var(--clue-border-mid)]" />
          </div>
        </>
      )}

      <p className="text-xs text-center text-[var(--clue-text-tertiary)]">
        Tap a term, then tap its match
      </p>

      <div className="grid grid-cols-2 gap-2">
        {/* Left column */}
        <div className="space-y-2">
          {screen.pairs.map(([left], i) => {
            const isMatched = matched.has(i);
            const isSelected = selectedLeft === i;
            const isWrong = wrongPair?.left === i;
            return (
              <button
                key={`l-${i}`}
                onClick={() => handleLeftTap(i)}
                disabled={isMatched}
                className={`w-full text-left px-3 py-2.5 rounded-lg border text-xs leading-snug transition-all duration-200 ${
                  isMatched
                    ? 'bg-[var(--clue-green-accent)]/10 border-[var(--clue-green-accent)]/30 text-[var(--clue-green-accent)]'
                    : isWrong
                      ? 'border-red-400 bg-red-500/10'
                      : isSelected
                        ? 'border-[var(--clue-green-accent)] bg-[var(--clue-green-accent)]/10 ring-1 ring-[var(--clue-green-accent)]'
                        : 'border-[var(--clue-border-mid)] bg-[var(--clue-bg-primary)] hover:border-[var(--clue-green-accent)]/50'
                }`}
              >
                {left}
              </button>
            );
          })}
        </div>

        {/* Right column (shuffled) */}
        <div className="space-y-2">
          {shuffledRightIndices.map((origIdx) => {
            const [, right] = screen.pairs[origIdx];
            const isMatched = matched.has(origIdx);
            const isSelected = selectedRight === origIdx;
            const isWrong = wrongPair?.right === origIdx;
            return (
              <button
                key={`r-${origIdx}`}
                onClick={() => handleRightTap(origIdx)}
                disabled={isMatched}
                className={`w-full text-left px-3 py-2.5 rounded-lg border text-xs leading-snug transition-all duration-200 ${
                  isMatched
                    ? 'bg-[var(--clue-green-accent)]/10 border-[var(--clue-green-accent)]/30 text-[var(--clue-green-accent)]'
                    : isWrong
                      ? 'border-red-400 bg-red-500/10'
                      : isSelected
                        ? 'border-[var(--clue-teal-accent)] bg-[var(--clue-teal-accent)]/10 ring-1 ring-[var(--clue-teal-accent)]'
                        : 'border-[var(--clue-border-mid)] bg-[var(--clue-bg-primary)] hover:border-[var(--clue-teal-accent)]/50'
                }`}
              >
                {right}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
